import { supabase } from '@/lib/supabaseClient';
import {
  BugReportRecord,
  BugAttachmentRecord,
  BugStatus,
  BugDeviceInfo,
  BugStatusHistoryItem,
} from '@/types/database';

export const BUG_ATTACHMENTS_BUCKET = 'vsb-bug-attachments';

export interface SubmitBugParams {
  description: string;
  app_route: string;
  farm_id?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  device_info?: BugDeviceInfo;
  attachments?: File[];
}

export interface BugListFilters {
  status?: BugStatus | 'all';
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface BugListResult {
  bugs: BugReportRecord[];
  totalCount: number;
}

export const bugService = {
  /**
   * Submits a new bug report and uploads any attached screenshots.
   */
  async submitBugReport(params: SubmitBugParams): Promise<BugReportRecord> {
    const {
      description,
      app_route,
      farm_id,
      entity_type,
      entity_id,
      device_info,
      attachments = [],
    } = params;

    // 1. Get current authenticated user details
    const { data: authData } = await supabase.auth.getUser();
    const user = authData.user;
    let reporterName = user?.email?.split('@')[0] || 'Anonymous';
    let reporterEmail = user?.email || undefined;

    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .maybeSingle();

      if (profile) {
        reporterName = profile.full_name || reporterName;
        reporterEmail = profile.email || reporterEmail;
      }
    }

    // 2. Insert bug_reports record
    const { data: bugRecord, error: bugError } = await supabase
      .from('bug_reports')
      .insert({
        description: description.trim(),
        reporter_id: user?.id || null,
        reporter_name: reporterName,
        reporter_email: reporterEmail,
        app_route: app_route || '/',
        farm_id: farm_id || null,
        entity_type: entity_type || null,
        entity_id: entity_id || null,
        device_info: device_info || {},
        status: 'new' as BugStatus,
        status_history: [
          {
            from_status: 'new',
            to_status: 'new',
            changed_by: user?.id || 'system',
            changed_by_name: reporterName,
            changed_at: new Date().toISOString(),
            notes: 'Bug report submitted',
          },
        ],
      })
      .select('*')
      .single();

    if (bugError || !bugRecord) {
      console.error('[bugService] Error creating bug report:', bugError);
      throw new Error(`Failed to save bug report: ${bugError?.message || 'Unknown error'}`);
    }

    const createdBug = bugRecord as BugReportRecord;

    // 3. Upload attachments sequentially if any
    const createdAttachments: BugAttachmentRecord[] = [];

    for (const file of attachments) {
      try {
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const timestamp = Date.now();
        const storagePath = `bugs/${createdBug.id}/${timestamp}_${sanitizedName}`;
        const mimeType = file.type || 'image/png';

        const { error: uploadError } = await supabase.storage
          .from(BUG_ATTACHMENTS_BUCKET)
          .upload(storagePath, file, {
            contentType: mimeType,
            upsert: false,
          });

        if (uploadError) {
          console.error('[bugService] Storage upload failed for attachment:', uploadError);
          continue;
        }

        const { data: attachRecord, error: attachDbError } = await supabase
          .from('bug_attachments')
          .insert({
            bug_id: createdBug.id,
            file_path: storagePath,
            file_name: file.name,
            file_size: file.size,
            mime_type: mimeType,
            created_by: user?.id || null,
          })
          .select('*')
          .single();

        if (attachDbError) {
          console.error('[bugService] Failed to insert attachment DB record:', attachDbError);
        } else if (attachRecord) {
          createdAttachments.push(attachRecord as BugAttachmentRecord);
        }
      } catch (err) {
        console.error('[bugService] Attachment processing error:', err);
      }
    }

    createdBug.attachments = createdAttachments;
    return createdBug;
  },

  /**
   * Fetches paginated bug reports list with optional status filter and search.
   */
  async getBugReports(filters: BugListFilters = {}): Promise<BugListResult> {
    const { status = 'all', search = '', page = 1, pageSize = 20 } = filters;

    let query = supabase
      .from('bug_reports')
      .select('*, bug_attachments(id, file_path, file_name, file_size, mime_type, created_at)', { count: 'exact' });

    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    if (search.trim()) {
      const term = search.trim();
      query = query.or(`bug_number.ilike.%${term}%,description.ilike.%${term}%,reporter_name.ilike.%${term}%,app_route.ilike.%${term}%`);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    query = query.order('created_at', { ascending: false }).range(from, to);

    const { data, count, error } = await query;

    if (error) {
      console.error('[bugService] Error fetching bug reports:', error);
      throw new Error(`Failed to load bug reports: ${error.message}`);
    }

    const bugs = (data || []).map((row: any) => ({
      ...row,
      attachments: row.bug_attachments || [],
    })) as BugReportRecord[];

    return {
      bugs,
      totalCount: count || 0,
    };
  },

  /**
   * Fetches a single bug report by ID with full attachment details and signed URLs.
   */
  async getBugReportById(bugId: string): Promise<BugReportRecord | null> {
    const { data, error } = await supabase
      .from('bug_reports')
      .select('*, bug_attachments(*)')
      .eq('id', bugId)
      .maybeSingle();

    if (error) {
      console.error('[bugService] Error fetching bug report by ID:', error);
      throw new Error(`Failed to load bug report: ${error.message}`);
    }

    if (!data) return null;

    const attachments: BugAttachmentRecord[] = [];
    const rawAttachments = data.bug_attachments || [];

    for (const att of rawAttachments) {
      let signed_url: string | null = null;
      try {
        const { data: signedData } = await supabase.storage
          .from(BUG_ATTACHMENTS_BUCKET)
          .createSignedUrl(att.file_path, 300); // 5 min TTL
        signed_url = signedData?.signedUrl || null;
      } catch (err) {
        console.error('[bugService] Error creating signed URL for attachment:', err);
      }

      attachments.push({
        ...att,
        signed_url,
      });
    }

    return {
      ...data,
      attachments,
    } as BugReportRecord;
  },

  /**
   * Updates bug report status, admin investigation notes, resolution notes, and records history.
   */
  async updateBugStatus(params: {
    bugId: string;
    status: BugStatus;
    adminNotes?: string;
    resolutionNotes?: string;
    statusNote?: string;
  }): Promise<BugReportRecord> {
    const { bugId, status, adminNotes, resolutionNotes, statusNote } = params;

    // 1. Get current record for history tracking
    const { data: currentBug, error: fetchErr } = await supabase
      .from('bug_reports')
      .select('status, status_history')
      .eq('id', bugId)
      .single();

    if (fetchErr || !currentBug) {
      throw new Error(`Failed to find bug report: ${fetchErr?.message || 'Not found'}`);
    }

    const { data: authData } = await supabase.auth.getUser();
    const user = authData.user;
    let changerName = user?.email?.split('@')[0] || 'Admin';

    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .maybeSingle();
      if (profile?.full_name) changerName = profile.full_name;
    }

    const history: BugStatusHistoryItem[] = Array.isArray(currentBug.status_history)
      ? currentBug.status_history
      : [];

    const newHistoryItem: BugStatusHistoryItem = {
      from_status: currentBug.status as BugStatus,
      to_status: status,
      changed_by: user?.id || 'admin',
      changed_by_name: changerName,
      changed_at: new Date().toISOString(),
      notes: statusNote || (currentBug.status !== status ? `Status changed to ${status}` : 'Notes updated'),
    };

    const updatePayload: any = {
      status,
      status_history: [...history, newHistoryItem],
      updated_at: new Date().toISOString(),
    };

    if (adminNotes !== undefined) updatePayload.admin_notes = adminNotes;
    if (resolutionNotes !== undefined) updatePayload.resolution_notes = resolutionNotes;

    if (status === 'closed' || status === 'ready_to_verify') {
      updatePayload.resolved_by = user?.id || null;
      updatePayload.resolved_at = new Date().toISOString();
    }

    const { data: updated, error: updateErr } = await supabase
      .from('bug_reports')
      .update(updatePayload)
      .eq('id', bugId)
      .select('*')
      .single();

    if (updateErr || !updated) {
      console.error('[bugService] Error updating bug status:', updateErr);
      throw new Error(`Failed to update bug report: ${updateErr?.message || 'Unknown error'}`);
    }

    return updated as BugReportRecord;
  },

  /**
   * Generates a temporary signed URL to view a bug attachment screenshot.
   */
  async getSignedUrl(filePath: string, expiresInSeconds = 300): Promise<string> {
    const { data, error } = await supabase.storage
      .from(BUG_ATTACHMENTS_BUCKET)
      .createSignedUrl(filePath, expiresInSeconds);

    if (error || !data?.signedUrl) {
      throw new Error(`Failed to create signed URL: ${error?.message || 'Unknown error'}`);
    }

    return data.signedUrl;
  },
};
